import { checkReportDownloadStatus, downloadReport, getExecutedReportData } from "@/Api/ReportApi";
import { CheckReportDownloadStatusInputSchema } from "@/Schemas/Reports/CheckReportDownloadStatusInput";
import { GetMyReportsExecutionHistoryDto } from "@/Schemas/Reports/GetMyReportsExecutionHistoryDto";
import { Box, CircularProgress, IconButton, Typography, useTheme } from "@mui/material";
import { useEffect } from "react";
import { FileIcon, SnackbarProvider, SnackbarType, getCenterStyles, getFluidBoxStyles, getYCenterStyles, useTask } from "vig-ui-library";
import { DataNotFound } from "../DataNotFound";
import { ReportDownloadFileDto } from "@/Schemas/Reports/CheckReportDownloadStatusOutput";
import { IoCloudDownloadOutline } from "react-icons/io5";
import { DownloadReportInputSchema } from "@/Schemas/Reports/DownloadReportInput";
import { saveAs } from 'file-saver';
import { handleDevelopmentError } from "@/Utils/error";
import { GetExecutedReportDataInputSchema } from "@/Schemas/Reports/GetExecutedReportDataInput";

export type FilesToDownloadProps = {
    executedReport: GetMyReportsExecutionHistoryDto;
}

export const FilesToDownload = (props: FilesToDownloadProps) => {
    const { openSnackbar } = SnackbarProvider.useSnackbar();

    const { value, isRunning, perform } = useTask(async (signal: AbortSignal) => {
        try {
            const input = CheckReportDownloadStatusInputSchema.parse({
                id: props.executedReport.id,
            });
            return checkReportDownloadStatus(input, { signal });
        } catch (err: any) {
            if (err.code === "ERR_CANCELED") return;
            openSnackbar({
                text: "Nepodarilo sa načítať dáta, skúste to znova.",
                type: SnackbarType.ERROR,
            });
        }
    });

    useEffect(() => {
        const abortController = new AbortController();
        const signal: AbortSignal = abortController.signal;
        perform(signal);
        return () => {
            abortController.abort();
        }
    }, [])

    return (
        <Box sx={{ padding: 2, flexGrow: 1, height: "100%" }}>
            {isRunning && (
                <Box sx={{ ...getFluidBoxStyles(), ...getCenterStyles() }}>
                    <CircularProgress />
                </Box>
            )}

            {!isRunning && !value?.files?.length && (
                <DataNotFound
                    title="Žiadne súbory na stiahnutie"
                    description="Pre túto zostavu nie sú k dispozícii žiadne súbory na stiahnutie."
                />
            )}

            {!isRunning && !!value?.files?.length && <FilesList files={value.files} reportId={props.executedReport.id} />}
        </Box>
    );
}

type FilesListProps = {
    files: ReportDownloadFileDto[];
    reportId: string;
}

const FilesList = ({ files, reportId }: FilesListProps) => {
    return (
        <Box sx={{ display: "flex", flexDirection: "column" }}>
            {files.map((file, index) => <FileToDownload key={index} file={file} reportId={reportId} />)}
        </Box>
    );
}

type FileToDownloadProps = {
    file: ReportDownloadFileDto;
    reportId: string;
}

const FileToDownload = ({ file, reportId }: FileToDownloadProps) => {
    const theme = useTheme();

    let extension = file.reportsFilePath?.split('.').pop() || "";

    if (!extension) extension = file.reportName?.split('.').pop() || "";

    const { perform, isRunning } = useTask(async (file: ReportDownloadFileDto) => {
        try {
            const input = DownloadReportInputSchema.parse({
                reportHistoryId: reportId,
                type: file.fileType,
                group: file.group,
            });

            if (!file.reportExists) {
                const executedReportDataInput = GetExecutedReportDataInputSchema.parse({
                    reportDownloadType: file.fileType,
                    reportHistoryId: reportId,
                    group: file.group,
                    generateFileIfNotExists: true,
                });
                await getExecutedReportData(executedReportDataInput);
            }

            const response = await downloadReport(input);

            if (!response.file) return;

            const byteCharacters = atob(response.file);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);

            const blob = new Blob([byteArray], { type: response.mime.mimeType });

            saveAs(blob, response.name || "report unknown");

            return response;
        } catch (err) {
            handleDevelopmentError(err);
        }
    }, { drop: true });

    const listItemStyles = {
        ...getYCenterStyles(),
        gap: 2,
        py: 1,
        px: 2,
        borderBottom: `1px solid ${theme.palette.divider}`,
        transition: "background-color 0.2s",
        pointerEvents: isRunning ? "none" : "auto",
        cursor: "pointer",
        "&:hover": {
            backgroundColor: theme.palette.grey[50],
        }
    }

    const downloadFile = (e: React.MouseEvent) => {
        e.stopPropagation();
        perform(file);
    }

    return (
        <Box sx={listItemStyles} onClick={downloadFile}>
            <FileIcon size="large" extension={extension} />
            <Box flexGrow={1}>
                <Typography variant="subtitle1">{file.reportName}</Typography>
            </Box>
            {isRunning && (<CircularProgress />)}
            {!isRunning && (
                <IconButton color="primary" onClick={downloadFile}>
                    <IoCloudDownloadOutline />
                </IconButton>
            )}
        </Box>
    );
}